import { useState } from "react";
import { TopBar } from "../shared/TopBar";
import { Loader2, Trash2Icon, XIcon } from "lucide-react";
import { Button } from "@stackje/shared-components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@stackje/shared-components/ui/dialog";
import { EditCardContent } from "./EditCardContent";
import { UnsavedChangesDialog } from "../shared/UnsavedChangesDialog";
import type { Card } from "@stackje/common/config";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { captureErrorAndToast } from "@/utils/error-utils";
import { useNavigate } from "@tanstack/react-router";

export function EditCardPage({
  card,
  cardIndex,
  onSubmit,
  onDelete,
}: {
  card: Card;
  cardIndex: number;
  onSubmit?: (formData: FormData) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [actionData] = useState<{
    validationErrors?: string[];
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hasFormInputChanges, setHasFormInputChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete?.();
      toast({
        description: "Card deleted successfully",
      });
      setHasFormInputChanges(false);
      navigate({ to: "/" });
    } catch (e) {
      captureErrorAndToast(e, toast, {
        description: `${e}`
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmitSuccess = async (formData: FormData) => {
    setIsSubmitting(true);
    await onSubmit?.(formData);
    setIsSubmitting(false);
  };

  const handleBackClick = () => {
    history.back();
  };


  return (
    <div className="pt-6 lg:pt-10">
      <TopBar>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
          className="text-gray-600 hover:text-gray-900"
        >
          <XIcon className="h-5 w-5" />
        </Button>
        <h1 className="text-center">Edit Content</h1>
        <Dialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
        >
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Delete">
              <Trash2Icon className="h-5 w-5" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[425px] w-[85vw] sm:w-[65vw] md:w-[55vw]">
            <DialogHeader>
              <DialogTitle>Delete Card</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this card? This action
                cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="w-full"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TopBar>

      <div className="w-full mt-8">
        <div className="mx-auto">
          {actionData?.validationErrors && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h3 className="text-red-800 font-medium mb-2">
                Validation Errors:
              </h3>
              <ul className="list-disc list-inside text-red-700">
                {actionData.validationErrors.map(
                  (error: string, index: number) => (
                    <li key={index}>{error}</li>
                  )
                )}
              </ul>
            </div>
          )}
          <EditCardContent
            card={card}
            cardIndex={cardIndex}
            onFormInputChange={() => setHasFormInputChanges(true)}
            onSubmit={handleSubmitSuccess}
          />
        </div>
      </div>

      <UnsavedChangesDialog
        isDirty={hasFormInputChanges}
        disabled={isSubmitting}
      />
    </div>
  );
}
